import HttpService from '@/common/services/HttpService'
import Keycloak, { type KeycloakConfig, type KeycloakLoginOptions } from 'keycloak-js'
import router from '@/router'
import type { AuthenticationConfigDTOV1 } from '@/common/types/authenticationConfig'
import configResolver from '@/common/helpers/configResolver'
import { ADMIN_ROUTE_PATHS } from '@/admin/types/router'
import { APP_ROUTE_NAMES } from '@/app/types/router'
import { extractErrorMessage } from '@/common/helpers/errorUtils'
import { useSessionStore } from '@/common/stores/sessionStore'
import { resetNavigationHistory } from '@/common/services/NavigationService'

const LOGOUT_PATH = '/assist/login?logout'
const AUTH_CONFIG_PATH = '/api/v1/auth/config'
const OIDC_SESSION_LINK_API = '/api/v1/oidc-session-links'
const KEYCLOAK_TOKEN = 'kc_token'
const KEYCLOAK_REFRESH_TOKEN = 'kc_refreshToken'

let keycloakInstanceRef: Keycloak | undefined
let manageOidcSession: boolean // call createOidcSession, updateOidcSession, removeOidcSession

export const OIDC_SECURED_APIS_BEFORE_SESSION_INIT = ['/api/v1/backend-event/token']
export const MENU_ITEMS_DISABLED_FOR_OIDC: Array<string> = [ADMIN_ROUTE_PATHS.ADMIN_AUTH_SETTINGS_ROUTE]

// Function to apply authentication
export const keycloakAuthentication = async (withOidcSession = true): Promise<Keycloak | void> => {
  const authenticationConfig = await getAuthenticationConfig()
  if (!isValidOidcProviderUrl(authenticationConfig.oidcProviderUrl)) return
  const keycloak = await initKeycloak(authenticationConfig)
  if (!keycloak.authenticated) {
    throw new Error('Keycloak authentication failed.')
  }
  keycloakInstanceRef = keycloak
  manageOidcSession = withOidcSession
  if (manageOidcSession && isFirstConnection()) {
    await createOidcSession()
  }
  updateKeycloakSessionStorage(keycloak)
  return keycloak
}

// Function to handle logout
export const logout = async () => {
  resetNavigationHistory()

  if (keycloakInstanceRef) {
    await router.push({ name: APP_ROUTE_NAMES.OIDC_LOGOUT_ROUTE_NAME })
  } else {
    // else use legacy logout
    await HttpService.delete('/api/v1/users/me')
    await router.push(LOGOUT_PATH)
  }
}

// Function to handle oidc logout
export const oidcLogout = async () => {
  try {
    try {
      if (manageOidcSession || keycloakInstanceRef) await removeOidcSession()
      clearKeycloakSessionStorage()
    } catch (error) {
      console.error('Error removeOidcSession: ', error)
    } finally {
      await keycloakInstanceRef?.logout()
    }
  } catch (error) {
    console.error('Error keycloak logout : ', error)
  }
}

const initKeycloak = async (authenticationConfig: AuthenticationConfigDTOV1): Promise<Keycloak> => {
  const keycloakConfig: KeycloakConfig = {
    url: authenticationConfig.oidcProviderUrl,
    realm: authenticationConfig.tenantId,
    clientId: 'web-end-user-client',
  }
  const keycloak = new Keycloak(keycloakConfig)
  const authenticated = await keycloak.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    token: getCurrentKeycloakToken()!,
    refreshToken: getCurrentKeycloakRefreshToken()!,
  })
  if (authenticated) {
    handleTokenExpired(keycloak)
  }
  return keycloak
}

function updateKeycloakSessionStorage(keycloak: Keycloak) {
  sessionStorage.setItem(KEYCLOAK_TOKEN, <string>keycloak.token)
  sessionStorage.setItem(KEYCLOAK_REFRESH_TOKEN, <string>keycloak.refreshToken)
}

function clearKeycloakSessionStorage() {
  sessionStorage.removeItem(KEYCLOAK_TOKEN)
  sessionStorage.removeItem(KEYCLOAK_REFRESH_TOKEN)
}

function isFirstConnection(): boolean {
  return !getCurrentKeycloakToken()?.trim()
}

export function getCurrentKeycloakToken(): string | null {
  return sessionStorage.getItem(KEYCLOAK_TOKEN)
}

export async function triggerOidcChangePassword(redirectUri = '') {
  const keycloakLoginOptions: KeycloakLoginOptions = { action: 'UPDATE_PASSWORD', redirectUri: redirectUri, prompt: 'login' }
  await keycloakInstanceRef?.login(keycloakLoginOptions)
}

function getCurrentKeycloakRefreshToken(): string | null {
  return sessionStorage.getItem(KEYCLOAK_REFRESH_TOKEN)
}

// init backend session from Keycloak token
const createOidcSession = async () => {
  try {
    await HttpService.post(OIDC_SESSION_LINK_API, {}, { headers: { Authorization: `Bearer ${keycloakInstanceRef?.token}` } })
  } catch (error) {
    const errorMessage = extractErrorMessage(error)
    const errorPage = `${configResolver.root}assist/error?errorMessage=${errorMessage}`
    //remove current keycloak session
    clearKeycloakSessionStorage()
    await keycloakInstanceRef?.logout({ redirectUri: errorPage })
  }
}
// update backend session with Keycloak token
const updateOidcSession = async () => {
  await HttpService.put(OIDC_SESSION_LINK_API)
}
// remove backend session
const removeOidcSession = async () => {
  await HttpService.delete(OIDC_SESSION_LINK_API)
}
// Function to handle token refresh
const handleTokenExpired = (keycloak: Keycloak) => {
  setInterval(async () => {
    try {
      const refreshed = await keycloak.updateToken(60) // If the token expires within 60 seconds
      if (refreshed) {
        updateKeycloakSessionStorage(keycloak)
        if (manageOidcSession) {
          await updateOidcSession()
        }
      }
    } catch (error) {
      console.error('Failed to refresh access token', error)
    }
  }, 60000) // Check access token refresh every 60 seconds
}

// check if oidc provider url is valid
function isValidOidcProviderUrl(oidcUrl: string | undefined | null): boolean {
  return typeof oidcUrl === 'string' && oidcUrl.trim() !== ''
}

// Function to get authentication configuration from backend
function getAuthenticationConfig(): Promise<AuthenticationConfigDTOV1> {
  return HttpService.getData<AuthenticationConfigDTOV1>(AUTH_CONFIG_PATH)
}

const isTargetUrlEligibleToTokenPropagation = function (originUrl: string, targetUrl: string): boolean {
  // Not starting with http -> eligible
  if (!targetUrl.startsWith('http')) return true
  // With same host, eligible
  const origin = new URL(originUrl)
  if (targetUrl.includes(origin.host)) return new URL(targetUrl).host === origin.host
  // Otherwise, no token propagated
  return false
}

export const isAccessTokenPropagationToRequestEnabled = function (originUrl: string, targetUrl: string): boolean {
  if (OIDC_SECURED_APIS_BEFORE_SESSION_INIT.includes(targetUrl)) return true // Manage early stage url that need token propagation
  if (!useSessionStore().oidcEnabled) return false
  return isTargetUrlEligibleToTokenPropagation(originUrl, targetUrl)
}
